import React, { useState } from "react";
import TrackHistoryUploader from "./TrackHistoryUploader";
import "./ChargebackUploadModal.css";
import { disputesApi } from "../services/disputes";
import { t } from "../i18n";

// Reactive chargeback-evidence upload, opened from the ⚠ icon on a
// PayoutHistoryPage row. Posts to /api/disputes/upload-for-payout/:batchId
// — the server figures out which open disputes in the batch need the
// file and attaches it to each one (one Stripe Files upload, fan-out).
//
// Visually distinct from CashoutProtectModal: orange/red urgency cues
// instead of green "you're protected" cues. Same TrackHistoryUploader
// inside — the talent's reading of the dropzone shouldn't change just
// because we're here for a different reason.

interface Props {
  batchId: string;
  pendingCount: number;
  onClose: () => void;
  onUploaded: () => void;
}

const ChargebackUploadModal: React.FC<Props> = ({
  batchId,
  pendingCount,
  onClose,
  onUploaded,
}) => {
  const [done, setDone] = useState(false);

  return (
    <div className="chargeback-upload-overlay" onClick={done ? onClose : undefined}>
      <div className="chargeback-upload-modal" onClick={(e) => e.stopPropagation()}>
        {!done ? (
          <>
            <div className="chargeback-upload-header">
              <div className="chargeback-upload-icon" aria-hidden>⚠️</div>
              <div className="chargeback-upload-eyebrow">{t("chargebackUploadModal.actionNeeded")}</div>
              <h2 className="chargeback-upload-title">
                {t("chargebackUploadModal.customerDisputedOrders", { count: pendingCount })}
              </h2>
              <p className="chargeback-upload-subtitle">
                {t("chargebackUploadModal.uploadYourSetS")}
              </p>
            </div>

            <div className="chargeback-upload-body">
              <TrackHistoryUploader
                upload={(fd) => disputesApi.uploadForPayout(batchId, fd)}
                submitLabel={t("chargebackUploadModal.submitEvidenceForOrders", { count: pendingCount })}
                onCancel={onClose}
                onUploaded={() => setDone(true)}
              />
            </div>
          </>
        ) : (
          <>
            <div className="chargeback-upload-header">
              <div className="chargeback-upload-icon chargeback-upload-icon--done" aria-hidden>✓</div>
              <h2 className="chargeback-upload-title">{t("chargebackUploadModal.evidenceSubmitted")}</h2>
              <p className="chargeback-upload-subtitle">
                {t("chargebackUploadModal.attachedToDisputedOrders", { count: pendingCount })}
              </p>
            </div>
            <div className="chargeback-upload-actions">
              <button
                type="button"
                className="chargeback-upload-primary"
                onClick={() => {
                  onUploaded();
                }}
              >
                {t("chargebackUploadModal.done")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChargebackUploadModal;
